import { createApiClient } from './client';
import { ApiError } from './errors';
import { useAuth } from '@/stores/auth';
import { useModeration } from '@/stores/moderation';
import { useGuidelinesGate } from '@/stores/guidelines';
import { useFinishProfile } from '@/stores/finishProfile';
import { serverClock } from '@/lib/clock';
import { normalizeDate, normalizeGame, normalizeMessage } from './normalize';
import type {
  // v5
  AdEligibility,
  AdEligibilityQuery,
  ClientEvent,
  ImpressionPlacement,
  RewardedPlacement,
  RewardedStart,
  AdminMetrics,
  FeatureFlag,
  AppConfig,
  AuthResponse,
  BlockEntry,
  Catalog,
  Connection,
  CreateDropInput,
  Decision,
  Drop,
  GameAction,
  GameType,
  GameView,
  Hangout,
  Intent,
  Invite,
  LiveStats,
  MatchStatus,
  Me,
  ModAction,
  ModActionType,
  ModReport,
  ModUserDetail,
  AdminUserPage,
  AdminDatePage,
  AdminDateDetail,
  AdminInvitePage,
  AdminUserQuery,
  Mood,
  Pod,
  QueueResponse,
  Session,
  VideoToken,
  Avatar,
  Availability,
  BillingPlan,
  ChatSummary,
  FriendRequest,
  Gender,
  MatchFilters,
  Message,
  PlanId,
  RecentPerson,
  Subscription,
  // v3
  ChallengeInput,
  ChallengeType,
  ChallengeView,
  Daily,
  GuessQuestion,
  IcebreakerMore,
  IcebreakerPrefs,
  PromptDepth,
  Proposal,
  VibeTag,
  WarmupChoice,
  // v4
  DetectionInput,
  DetectionStatus,
  SafetyDetection,
  SafetyEventName,
  // v6
  DateAction,
  DateLength,
  DateSignals,
  DateView,
  Suggestion,
  SuggestionOutcome,
  // v7
  InsightsCoverage,
  InsightsView,
  // v6.1
  CreateDateInvite,
  DateInvite,
  DateInvitePreview,
  PlannerPatch,
  RescheduleDateInvite,
  LearnedMore,
  VenueChoice,
  DiagTrace,
  // v9 area A
  DateSeries,
  LinkPatch,
  LinkRequest,
  MyLink,
  Photo,
  ProposeSeries,
  PublicLink,
  SeriesAction,
  // v8
  Gift,
  GiftCatalog,
  LedgerEntry,
  SendGiftInput,
  Shelf,
  VoidReason,
  Wallet,
  PrivateBrowsing,
  ProfileViews,
  TrafficEvent,
  TrafficReport,
  TryFunnelReport,
  TrafficSite,
  WaitlistList,
} from './types';


/** v14 diagnostics: what the phone saw go wrong (Admin → What happened?). */
export type DiagKind =
  | 'start_tap' | 'start_error' | 'face_blocked' | 'media_denied' | 'call_connect_error' | 'call_reconnecting' | 'call_disconnected' | 'date_load_error'
  // A browser error during a call (detail "<where>: <message>"), and the live connection coming back ("down 23 s").
  | 'game_error' | 'screen_error' | 'script_error' | 'live_reconnected'
  // The Photo booth closed early on this phone (detail "<reason>[: <why>]").
  | 'booth_closed';
export interface DiagEvent {
  kind: DiagKind;
  invite_id?: string;
  session_id?: string;
  /** An error code or a short message (≤ 120 characters; never personal text). */
  detail?: string;
}

/** Report a date problem; never throws, never blocks the UI. */
export function reportDiag(ev: DiagEvent): void {
  try {
    void endpoints.diag({ ...ev, detail: ev.detail?.slice(0, 120) }).catch(() => undefined);
  } catch {
    /* reporting must never break the flow it reports on */
  }
}
export const api = createApiClient({
  fetch: (...args) => globalThis.fetch(...args),
  baseUrl: '',
  getToken: () => useAuth.getState().accessToken,
  onRefreshed: (r) => useAuth.getState().setSession(r.access_token, r.user),
  onAuthLost: () => useAuth.getState().clear(),
  onSuspended: (e: ApiError) => {
    const until = typeof e.details.until === 'string' ? e.details.until : null;
    // v4: details.message carries the user-facing reason (e.g. the automated-safety copy). Never evidence.
    const reason = typeof e.details.message === 'string' && e.details.message ? e.details.message : e.message;
    useModeration.getState().setBlocked({ action: until ? 'suspend' : 'ban', reason, until });
  },
  onError: (e: ApiError) => {
    // Gated feature without a birthdate / finished onboarding (ARCHITECTURE §17):
    // refresh Me so the route guards send the user back to onboarding.
    const r = e.details.reason;
    if (e.code === 'invalid_state' && (r === 'birthdate_required' || r === 'not_onboarded')) {
      void api
        .request<Me>('/v1/me')
        .then((m) => useAuth.getState().setMe(m))
        .catch(() => undefined);
    }
    // v9 §A3: an express account hit Popin Live or filters — offer "Finish your profile".
    if (e.code === 'invalid_state' && r === 'profile_incomplete') useFinishProfile.getState().request();
    // v4 guidelines gate (SPEC_V4 §7): refresh Me and ask for the re-accept sheet.
    if (e.code === 'invalid_state' && r === 'guidelines_update') {
      void api
        .request<Me>('/v1/me')
        .then((m) => useAuth.getState().setMe(m))
        .catch(() => undefined);
      useGuidelinesGate.getState().request();
    }
  },
});

const req = api.request;

/** Every GameView carries server_time: feed the clock as soon as it arrives. */
function withClock(v: GameView): GameView {
  if (v?.server_time) serverClock.sample(v.server_time);
  return normalizeGame(v);
}

export interface MePatch {
  display_name?: string;
  bio?: string;
  interests?: string[];
  avatar?: Avatar;
  default_mood?: Mood;
  settings?: SettingsPatch;
  /** v6.2: the browser's IANA zone (date emails render in it). */
  timezone?: string;
  accept_guidelines?: true;
  complete_onboarding?: true;
  /** v9 §A3: complete onboarding the express way (with complete_onboarding). */
  express?: ExpressSource;
  // v2
  birthdate?: string;
  gender?: Gender;
  country?: string | null;
  show_country?: boolean;
}

/** `PATCH /v1/me/settings` body (also `settings` inside `PATCH /v1/me`). */
export interface SettingsPatch {
  availability?: Availability;
  allow_invites?: boolean;
  icebreakers?: Partial<IcebreakerPrefs>;
  /** v7 (SPEC_V7 §2): off stops Popin learning from and using your activity. */
  personalization?: boolean;
  /** v6.2: "Email me about dates". */
  email_dates?: boolean;
  /** v8: "Allow gifts" / "Let gifts add effects on my video". */
  allow_gifts?: boolean;
  gift_effects?: boolean;
}

export type EndReasonInput = 'skip' | 'hangup' | 'no_face' | 'safety';

export interface ReportInput {
  reported_user_id: string;
  reason: string;
  details?: string;
  session_id?: string;
  pod_id?: string;
  block?: boolean;
  message_ids?: number[];
}

/** A proposal call either creates a proposal (201) or, when the peer had already proposed the same game, starts it (200 GameView). */
export type ProposeResult = { kind: 'proposal'; proposal: Proposal } | { kind: 'game'; game: GameView };

export function isGameView(x: unknown): x is GameView {
  return !!x && typeof x === 'object' && 'players' in x && 'phase' in x;
}

/** v6: a DateView (vs a GameView or Proposal) — accept/propose for `kind:'date'` return one. */
export function isDateView(x: unknown): x is DateView {
  return !!x && typeof x === 'object' && 'acts' in x && 'length_min' in x;
}

/** v6: a date proposal either creates a proposal or (when they'd already proposed one) starts the date. */
export type ProposeDateResult = { kind: 'proposal'; proposal: Proposal } | { kind: 'date'; date: DateView };

/** Every DateView carries server_time: feed the clock, then normalize. */
export function withDateClock(v: DateView | null | undefined): DateView | null {
  if (v?.server_time) serverClock.sample(v.server_time);
  return normalizeDate(v);
}

const msg = (m: Message) => normalizeMessage(m);

/** POST /v1/auth/guest (the 18+ tick and the guidelines are sent as accepted: the form requires them). */
export interface GuestAuthInput {
  name: string;
  visitor: string;
  source: 'try' | 'invite';
}

/** v9 §A3: where an express guest came from ('try': sent their own invite from /try, docs/SPEC_TRY.md). */
export type ExpressSource = 'invite' | 'link' | 'direct' | 'try';

export const endpoints = {
  // public
  config: () => req<AppConfig>('/v1/config', { auth: false }),
  catalog: () => req<Catalog>('/v1/catalog', { auth: false }),

  // auth (SPEC_V2 §1: Google only)
  /** v9 §A3: `express` marks a guest arriving from a link (express onboarding). */
  // A guest session's token goes along (auth 'optional'): the server turns
  // the guest into the Google account instead of making a new one.
  googleAuth: (credential: string, express?: ExpressSource) =>
    req<AuthResponse>('/v1/auth/google', { method: 'POST', body: express ? { credential, express_source: express } : { credential }, auth: 'optional' }),
  /** docs/SPEC_TRY.md: a guest account (first name + 18+), no Google. */
  guestAuth: (b: GuestAuthInput) => req<AuthResponse>('/v1/auth/guest', { method: 'POST', body: { ...b, adult: true, accept_guidelines: true }, auth: false }),
  /** After the call: a claimed guest hands its dates to its Google account. */
  guestSwitch: () => req<AuthResponse>('/v1/auth/guest/switch', { method: 'POST' }),
  refresh: () => api.refresh(),
  logout: () => req<void>('/v1/auth/logout', { method: 'POST', auth: false }),
  wsTicket: () => req<{ ticket: string; expires_in: number }>('/v1/ws/ticket', { method: 'POST' }),

  // me
  me: () => req<Me>('/v1/me'),
  updateMe: (patch: MePatch) => req<Me>('/v1/me', { method: 'PATCH', body: patch }),
  deleteMe: () => req<void>('/v1/me', { method: 'DELETE', body: { confirm: 'DELETE' } }),
  setFilters: (f: MatchFilters) => req<Me>('/v1/me/filters', { method: 'PUT', body: f }),
  exportMe: () => req<Response>('/v1/me/export', { raw: true }),
  updateSettings: (s: SettingsPatch) => req<Me>('/v1/me/settings', { method: 'PATCH', body: s }),
  // v7 person insights (SPEC_V7 §3). `tz` (IANA) localises the "most around" card.
  insights: (tz?: string) => req<InsightsView>(`/v1/me/insights${tz ? `?tz=${encodeURIComponent(tz)}` : ''}`),
  resetInsights: () => req<void>('/v1/me/insights', { method: 'DELETE' }),
  live: () => req<LiveStats>('/v1/live'),

  // matchmaking
  /** v6: `intent:'date'` carries `date_length_min` (SPEC_V6 §7). */
  enqueue: (mood: Mood, intent: Intent, useFilters = true, dateLengthMin?: DateLength) =>
    req<QueueResponse>('/v1/match/queue', {
      method: 'POST',
      body: { mood, intent, ...(useFilters ? {} : { use_filters: false }), ...(intent === 'date' ? { date_length_min: dateLengthMin ?? 30 } : {}) },
    }),
  leaveQueue: () => req<void>('/v1/match/queue', { method: 'DELETE' }),
  matchStatus: () => req<MatchStatus>('/v1/match/status'),

  // sessions
  currentSession: () => req<Session>('/v1/sessions/current'),
  session: (id: string) => req<Session>(`/v1/sessions/${id}`),
  sessionToken: (id: string) => req<VideoToken>(`/v1/sessions/${id}/token`, { method: 'POST' }),
  joinSession: (id: string) => req<Session>(`/v1/sessions/${id}/join`, { method: 'POST' }),
  endSession: (id: string, reason: EndReasonInput) => req<Session>(`/v1/sessions/${id}/end`, { method: 'POST', body: { reason } }),
  feedback: (id: string, decision: Decision, rating?: number | null) =>
    req<Session>(`/v1/sessions/${id}/feedback`, { method: 'POST', body: rating ? { decision, rating } : { decision } }),
  /** Pods only since v3 — calls use proposals (SPEC_V3 §3). */
  startSessionGame: (id: string, type: GameType) =>
    req<GameView>(`/v1/sessions/${id}/games`, { method: 'POST', body: { type } }).then(withClock),

  // v3: proposals, ideas, warm-up, vibes (SPEC_V3 §3–§8)
  /** v6: `quick` asks for the ≈2-minute variant (Free Flow suggestions). */
  proposeGame: (sessionId: string, type: GameType, opts: { quick?: boolean } = {}): Promise<ProposeResult> =>
    req<Proposal | GameView>(`/v1/sessions/${sessionId}/game-proposals`, { method: 'POST', body: opts.quick ? { type, quick: true } : { type } }).then((r) =>
      isGameView(r) ? { kind: 'game', game: withClock(r) } : { kind: 'proposal', proposal: r },
    ),
  /** v6: "Make it a date" (SPEC_V6 §3): `{type:'date', length_min}`, stored with `kind:'date'`. */
  proposeDate: (sessionId: string, lengthMin: DateLength): Promise<ProposeDateResult> =>
    req<Proposal | DateView>(`/v1/sessions/${sessionId}/game-proposals`, { method: 'POST', body: { type: 'date', length_min: lengthMin } }).then((r) =>
      isDateView(r) ? { kind: 'date', date: withDateClock(r)! } : { kind: 'proposal', proposal: r },
    ),
  /** Accepting a game proposal returns its GameView; a date proposal (v6) returns the DateView. */
  acceptProposal: (id: string): Promise<GameView | DateView> =>
    req<GameView | DateView>(`/v1/game-proposals/${id}/accept`, { method: 'POST' }).then((r) => (isDateView(r) ? withDateClock(r)! : withClock(r))),
  declineProposal: (id: string) => req<Proposal>(`/v1/game-proposals/${id}/decline`, { method: 'POST' }),
  cancelProposal: (id: string) => req<Proposal>(`/v1/game-proposals/${id}/cancel`, { method: 'POST' }),
  moreIcebreakers: (sessionId: string, depth: PromptDepth, exclude: string[] = []) =>
    req<IcebreakerMore>(
      `/v1/sessions/${sessionId}/icebreakers?depth=${depth}${exclude.length ? `&exclude=${exclude.map(encodeURIComponent).join(',')}` : ''}`,
    ),
  submitWarmup: (sessionId: string, choices: (WarmupChoice | null)[]) => req<Session>(`/v1/sessions/${sessionId}/warmup`, { method: 'POST', body: { choices } }),
  giveVibes: (sessionId: string, tags: VibeTag[]) => req<void>(`/v1/sessions/${sessionId}/vibes`, { method: 'POST', body: { tags } }),
  daily: () => req<Daily>('/v1/daily'),

  // v6: Free Flow suggestions + Date Night (SPEC_V6 §2, §7)
  suggestion: (sessionId: string) => req<Suggestion>(`/v1/sessions/${sessionId}/suggestion`),
  suggestionOutcome: (sessionId: string, suggestionId: string, outcome: SuggestionOutcome) =>
    req<void>(`/v1/sessions/${sessionId}/suggestion/${encodeURIComponent(suggestionId)}/outcome`, { method: 'POST', body: { outcome } }),
  /** `null` when no date (or the feature is off: 404 feature_disabled). */
  date: (sessionId: string) =>
    req<DateView | null>(`/v1/sessions/${sessionId}/date`)
      .then(withDateClock)
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }),
  dateAction: (sessionId: string, action: DateAction) => req<DateView>(`/v1/sessions/${sessionId}/date/actions`, { method: 'POST', body: action }).then(withDateClock),
  /** Own mic only — numbers, never audio (SPEC_V6 §0.4). */
  dateSignals: (sessionId: string, s: DateSignals) => req<void>(`/v1/sessions/${sessionId}/date/signals`, { method: 'POST', body: s }),
  /** v6.1 §7: the optional one-tap survey after a Date Night (aggregate only). */
  dateFeedback: (sessionId: string, learnedMore: LearnedMore) =>
    req<void>(`/v1/sessions/${sessionId}/date/feedback`, { method: 'POST', body: { learned_more: learnedMore } }),
  /** v9 B1: a random Date Night's intro vote (the server flips the coin on different picks). */
  voteVenue: (sessionId: string, venue: VenueChoice) => req<DateView>(`/v1/sessions/${sessionId}/date/venue`, { method: 'POST', body: { venue } }).then(withDateClock),

  // v6.1 Popin Dates invite links (SPEC_V6_1). The link (url) only comes back on create / new link.
  dateInvites: () => req<{ invites: DateInvite[] }>('/v1/date-invites'),
  dateInvite: (id: string) => req<DateInvite>(`/v1/date-invites/${id}`),
  createDateInvite: (b: CreateDateInvite) => req<DateInvite>('/v1/date-invites', { method: 'POST', body: b }),
  /** Public: works logged out (no Authorization header, no refresh-on-401). */
  dateInvitePreview: (token: string) => req<DateInvitePreview>(`/v1/date-invites/preview/${encodeURIComponent(token)}`, { auth: false }),
  acceptDateInvite: (token: string) => req<DateInvite>('/v1/date-invites/accept', { method: 'POST', body: { token } }),
  declineDateInviteLink: (token: string) => req<void>('/v1/date-invites/decline', { method: 'POST', body: { token } }),
  cancelDateInvite: (id: string) => req<DateInvite>(`/v1/date-invites/${id}/cancel`, { method: 'POST' }),
  declineDateInvite: (id: string) => req<DateInvite>(`/v1/date-invites/${id}/decline`, { method: 'POST' }),
  rescheduleDateInvite: (id: string, body: RescheduleDateInvite) =>
    req<DateInvite>(`/v1/date-invites/${id}/reschedule`, { method: 'POST', body }),
  /** v6.2 "Plan our date": partial saves. */
  saveDatePlanner: (id: string, body: PlannerPatch) => req<DateInvite>(`/v1/date-invites/${id}/planner`, { method: 'PUT', body }),
  /** v9 B1: the host picks the venue. */
  setDateInviteVenue: (id: string, venue: VenueChoice) => req<DateInvite>(`/v1/date-invites/${id}/venue`, { method: 'PUT', body: { venue } }),
  confirmDateInvite: (id: string, slotId: string) =>
    req<DateInvite>(`/v1/date-invites/${id}/confirm`, { method: 'POST', body: { slot_id: slotId } }),
  newDateInviteLink: (id: string) => req<DateInvite>(`/v1/date-invites/${id}/link`, { method: 'POST' }),
  lobbyHeartbeat: (id: string) => req<DateInvite>(`/v1/date-invites/${id}/lobby`, { method: 'POST' }),
  leaveLobby: (id: string) => req<void>(`/v1/date-invites/${id}/lobby`, { method: 'DELETE' }),
  /** v14: the phone's side of a date problem (internal/diag). Fire and forget. */
  diag: (body: DiagEvent) => req<void>(`/v1/diag`, { method: 'POST', body, dedupe: false }),
  startDateInvite: (id: string) => req<{ invite: DateInvite; session: Session }>(`/v1/date-invites/${id}/start`, { method: 'POST' }),

  answerDaily: (text: string, share: boolean) => req<Daily>('/v1/daily', { method: 'PUT', body: { text, share } }),

  // games
  game: (id: string) => req<GameView>(`/v1/games/${id}`).then(withClock),
  gameAction: (id: string, action: GameAction) =>
    req<GameView>(`/v1/games/${id}/actions`, { method: 'POST', body: { action } }).then(withClock),
  quitGame: (id: string) => req<GameView>(`/v1/games/${id}/quit`, { method: 'POST' }).then(withClock),

  // people
  connections: () => req<{ connections: Connection[] }>('/v1/connections'),
  removeConnection: (id: string) => req<void>(`/v1/connections/${id}`, { method: 'DELETE' }),
  invite: (connectionId: string) => req<Invite>(`/v1/connections/${connectionId}/invite`, { method: 'POST' }),
  invites: () => req<{ incoming: Invite[]; outgoing: Invite[] }>('/v1/invites'),
  acceptInvite: (id: string) => req<{ invite: Invite; session: Session }>(`/v1/invites/${id}/accept`, { method: 'POST' }),
  declineInvite: (id: string) => req<Invite>(`/v1/invites/${id}/decline`, { method: 'POST' }),
  cancelInvite: (id: string) => req<Invite>(`/v1/invites/${id}/cancel`, { method: 'POST' }),
  hangouts: () => req<{ hangouts: Hangout[] }>('/v1/hangouts'),
  createHangout: (b: { connection_id: string; title: string; weekday: number; start_time: string; timezone: string; duration_minutes: number }) =>
    req<Hangout>('/v1/hangouts', { method: 'POST', body: b }),
  deleteHangout: (id: string) => req<void>(`/v1/hangouts/${id}`, { method: 'DELETE' }),

  // v9 area A (SPEC_V9 §A, ARCHITECTURE §28): personal links and weekly date night.
  myLink: () => req<MyLink>('/v1/me/link'),
  // v12 (SPEC_V12): profile photos, private until a request or a yes.
  myPhotos: () => req<{ photos: Photo[] }>('/v1/me/photos'),
  addPhoto: (jpeg: Blob) => req<Photo>('/v1/me/photos', { method: 'POST', body: jpeg }),
  deletePhoto: (id: string) => req<void>(`/v1/me/photos/${id}`, { method: 'DELETE' }),
  orderPhotos: (ids: string[]) => req<{ photos: Photo[] }>('/v1/me/photos/order', { method: 'PUT', body: { ids } }),
  userPhotos: (id: string) => req<{ photos: Photo[] }>(`/v1/users/${id}/photos`),
  // v12.1: who opened my link; private browsing (only while the admin allows it).
  profileViews: () => req<ProfileViews>('/v1/me/profile-views'),
  setPrivateBrowsing: (on: boolean) => req<PrivateBrowsing>('/v1/me/private-browsing', { method: 'PUT', body: { on } }),
  saveMyLink: (patch: LinkPatch) => req<MyLink>('/v1/me/link', { method: 'PUT', body: patch }),
  /** Public; sends the token when signed in (blocked people see no times). */
  publicLink: (handle: string) => req<PublicLink>(`/v1/links/${encodeURIComponent(handle)}`),
  requestLinkSlot: (handle: string, b: { starts_at: string; length_min: DateLength; note?: string }) =>
    req<LinkRequest>(`/v1/links/${encodeURIComponent(handle)}/requests`, { method: 'POST', body: b }),
  linkRequests: () => req<{ incoming: LinkRequest[]; outgoing: LinkRequest[] }>('/v1/me/link/requests'),
  approveLinkRequest: (id: string) => req<{ request: LinkRequest; invite: DateInvite }>(`/v1/link-requests/${id}/approve`, { method: 'POST' }),
  declineLinkRequest: (id: string) => req<LinkRequest>(`/v1/link-requests/${id}/decline`, { method: 'POST' }),
  dateSeries: () => req<{ series: DateSeries[] }>('/v1/date-series'),
  proposeSeries: (b: ProposeSeries) => req<DateSeries>('/v1/date-series', { method: 'POST', body: b }),
  seriesAction: (id: string, action: SeriesAction) => req<DateSeries>(`/v1/date-series/${id}/${action}`, { method: 'POST' }),

  // safety
  blocks: () => req<{ blocks: BlockEntry[] }>('/v1/blocks'),
  block: (userId: string) => req<void>('/v1/blocks', { method: 'POST', body: { user_id: userId } }),
  unblock: (userId: string) => req<void>(`/v1/blocks/${userId}`, { method: 'DELETE' }),
  report: (b: ReportInput) => req<{ id: string }>('/v1/reports', { method: 'POST', body: b }),

  // friends (SPEC_V2 §5)
  recentPeople: () => req<{ people: RecentPerson[] }>('/v1/people/recent'),
  friendRequests: () => req<{ incoming: FriendRequest[]; outgoing: FriendRequest[] }>('/v1/friend-requests'),
  /** 201 FriendRequest, or 200 {connection} when they had already asked you (auto-accept). */
  sendFriendRequest: (userId: string) => req<FriendRequest | { connection: Connection }>('/v1/friend-requests', { method: 'POST', body: { user_id: userId } }),
  acceptFriendRequest: (id: string) => req<{ connection: Connection }>(`/v1/friend-requests/${id}/accept`, { method: 'POST' }),
  declineFriendRequest: (id: string) => req<FriendRequest>(`/v1/friend-requests/${id}/decline`, { method: 'POST' }),
  cancelFriendRequest: (id: string) => req<FriendRequest>(`/v1/friend-requests/${id}/cancel`, { method: 'POST' }),

  // chat (SPEC_V2 §6)
  chats: () => req<{ chats: ChatSummary[] }>('/v1/chats'),
  messages: (connectionId: string, before?: number, limit = 50) =>
    req<{ messages: Message[]; has_more: boolean }>(`/v1/chats/${connectionId}/messages?limit=${limit}${before ? `&before=${before}` : ''}`).then((r) => ({
      ...r,
      messages: (r?.messages ?? []).map(msg),
    })),
  sendMessage: (connectionId: string, body: string, clientId: string) =>
    req<Message>(`/v1/chats/${connectionId}/messages`, { method: 'POST', body: { body, client_id: clientId } }).then(msg),
  readChat: (connectionId: string, upToId: number) => req<void>(`/v1/chats/${connectionId}/read`, { method: 'POST', body: { up_to_id: upToId } }),
  unsendMessage: (connectionId: string, id: number) => req<Message>(`/v1/chats/${connectionId}/messages/${id}`, { method: 'DELETE' }).then(msg),

  // v3: chat challenges (SPEC_V3 §9)
  challengeOptions: <T extends ChallengeType>(type: T) =>
    req<T extends 'doodle' ? { words: string[] } : { questions: GuessQuestion[] }>(`/v1/challenges/options?type=${type}`),
  sendChallenge: (connectionId: string, input: ChallengeInput) => req<Message>(`/v1/chats/${connectionId}/challenges`, { method: 'POST', body: input }).then(msg),
  guessChallenge: (id: string, guess: { text: string } | { index: number }) => req<ChallengeView>(`/v1/challenges/${id}/guess`, { method: 'POST', body: guess }),

  // billing (SPEC_V2 §3)
  billingPlans: () => req<{ plans: BillingPlan[] }>('/v1/billing/plans'),
  /** `plus_day` (v5) is a one-time Checkout (mode=payment) for 24 hours of Plus. */
  checkout: (plan: PlanId) => req<{ url: string }>('/v1/billing/checkout', { method: 'POST', body: { plan } }),
  billingPortal: () => req<{ url: string }>('/v1/billing/portal', { method: 'POST' }),
  subscription: () => req<Subscription | null>('/v1/billing/subscription'),

  // v5: ads, rewards and allow-listed client events (SPEC_V5, ARCHITECTURE §21)
  adsRewardedStart: (placement: RewardedPlacement, dropId?: string) =>
    req<RewardedStart>('/v1/ads/rewarded/start', { method: 'POST', body: dropId ? { placement, drop_id: dropId } : { placement } }),
  adsRewardedComplete: (token: string) => req<Me>('/v1/ads/rewarded/complete', { method: 'POST', body: { token } }),
  adsEligibility: (q: AdEligibilityQuery) => {
    const p = new URLSearchParams({ placement: q.placement });
    if (q.session_id) p.set('session_id', q.session_id);
    if (q.drop_id) p.set('drop_id', q.drop_id);
    if (q.calls_since_last !== undefined) p.set('calls_since_last', String(Math.max(0, Math.floor(q.calls_since_last))));
    return req<AdEligibility>(`/v1/ads/eligibility?${p.toString()}`);
  },
  adsImpression: (placement: ImpressionPlacement) => req<void>('/v1/ads/impressions', { method: 'POST', body: { placement }, dedupe: false }),
  trackEvent: (e: ClientEvent) => req<void>('/v1/events', { method: 'POST', body: e, dedupe: false }),

  // drops
  drops: () => req<{ drops: Drop[] }>('/v1/drops'),
  drop: (id: string) => req<Drop>(`/v1/drops/${id}`),
  rsvp: (id: string) => req<Drop>(`/v1/drops/${id}/rsvp`, { method: 'POST' }),
  unrsvp: (id: string) => req<Drop>(`/v1/drops/${id}/rsvp`, { method: 'DELETE' }),
  joinDrop: (id: string) => req<Pod>(`/v1/drops/${id}/join`, { method: 'POST' }),
  pod: (id: string) => req<Pod>(`/v1/pods/${id}`),
  podToken: (id: string) => req<VideoToken>(`/v1/pods/${id}/token`, { method: 'POST' }),
  leavePod: (id: string) => req<void>(`/v1/pods/${id}/leave`, { method: 'POST' }),
  startPodGame: (id: string, type: GameType) =>
    req<GameView>(`/v1/pods/${id}/games`, { method: 'POST', body: { type } }).then(withClock),
  podConnect: (id: string, userId: string) => req<{ mutual: boolean }>(`/v1/pods/${id}/connect`, { method: 'POST', body: { user_id: userId } }),

  // v4: automated safety (SPEC_V4 §4). Frames go only to this endpoint, never anywhere else.
  submitDetection: (b: DetectionInput) => req<{ id: string }>('/v1/safety/detections', { method: 'POST', body: b }),
  safetyEvent: (name: SafetyEventName) => req<void>('/v1/safety/events', { method: 'POST', body: { name }, dedupe: false }),

  // moderation / admin
  modReports: (status = 'open') => req<{ reports: ModReport[] }>(`/v1/mod/reports?status=${encodeURIComponent(status)}`),
  modUser: (id: string) => req<ModUserDetail>(`/v1/mod/users/${id}`),
  // Admin → Users: the paginated list and moderator roles.
  adminUsers: (q: AdminUserQuery) => {
    const p = new URLSearchParams(Object.entries(q).filter(([, v]) => v !== undefined && v !== '' && v !== 'all').map(([k, v]) => [k, String(v)]));
    return req<AdminUserPage>(`/v1/admin/users${p.size ? `?${p}` : ''}`);
  },
  // Admin → Dates: who met whom and how it went.
  adminDates: (q: { days: number; q?: string; status?: string; page?: number; per_page?: number }) => {
    const p = new URLSearchParams(Object.entries(q).filter(([, v]) => v !== undefined && v !== '' && v !== 'all').map(([k, v]) => [k, String(v)]));
    return req<AdminDatePage>(`/v1/admin/dates?${p}`);
  },
  adminDate: (id: string) => req<AdminDateDetail>(`/v1/admin/dates/${id}`),
  /** v14: the whole story of one date, by invite or call id (internal/diag). */
  adminDiagTrace: (id: string) => req<DiagTrace>(`/v1/admin/diagnostics/dates/${id}`),
  adminInvites: (q: { days: number; q?: string; stage?: string; page?: number; per_page?: number }) => {
    const p = new URLSearchParams(Object.entries(q).filter(([, v]) => v !== undefined && v !== '' && v !== 'all').map(([k, v]) => [k, String(v)]));
    return req<AdminInvitePage>(`/v1/admin/date-invites?${p}`);
  },
  setUserRole: (id: string, role: 'user' | 'moderator') => req<ModUserDetail['user']>(`/v1/admin/users/${id}/role`, { method: 'PUT', body: { role } }),
  modAction: (b: { user_id: string; action: ModActionType; reason: string; duration_hours?: number; report_id?: string }) =>
    req<ModAction>('/v1/mod/actions', { method: 'POST', body: b }),
  dismissReport: (id: string) => req<void>(`/v1/mod/reports/${id}/dismiss`, { method: 'POST' }),
  // v4 detections (ARCHITECTURE §20)
  modDetections: (status: DetectionStatus | 'all' = 'pending') => req<{ detections: SafetyDetection[] }>(`/v1/mod/detections?status=${encodeURIComponent(status)}`),
  modDetection: (id: string) => req<SafetyDetection>(`/v1/mod/detections/${id}`),
  reviewDetection: (id: string, verb: 'confirm' | 'clear' | 'escalate', note?: string) =>
    req<SafetyDetection>(`/v1/mod/detections/${id}/${verb}`, { method: 'POST', body: note ? { note } : {} }),
  /** Raw JPEG (Authorization header via the client). Each call is access-logged server-side. */
  modEvidence: (frameId: string, signal?: AbortSignal) => req<Response>(`/v1/mod/evidence/${frameId}`, { raw: true, signal }),
  createDrop: (b: CreateDropInput) => req<Drop>('/v1/admin/drops', { method: 'POST', body: b }),
  cancelDrop: (id: string) => req<Drop>(`/v1/admin/drops/${id}/cancel`, { method: 'POST' }),
  metrics: (days: number) => req<AdminMetrics>(`/v1/admin/metrics?days=${days}`),
  // Landing traffic (docs/SPEC_TRAFFIC.md).
  traffic: (days: number, site: TrafficSite, tz: string) =>
    req<TrafficReport>(`/v1/admin/traffic?days=${days}&site=${site}&tz=${encodeURIComponent(tz)}`),
  trafficTry: (days: number, tz: string) => req<TryFunnelReport>(`/v1/admin/traffic/try?days=${days}&tz=${encodeURIComponent(tz)}`),
  trafficRecent: (site: TrafficSite) => req<{ events: TrafficEvent[] }>(`/v1/admin/traffic/recent?site=${site}&limit=50`),
  trafficExport: (days: number, site: TrafficSite) => req<Response>(`/v1/admin/traffic/export.csv?days=${days}&site=${site}`, { raw: true }),
  waitlist: (refresh = false) => req<WaitlistList>(`/v1/admin/waitlist${refresh ? '?refresh=1' : ''}`),
  waitlistExport: () => req<Response>('/v1/admin/waitlist.csv', { raw: true }),
  // runtime feature flags (ARCHITECTURE §22), admin only
  adminFlags: () => req<{ flags: FeatureFlag[] }>('/v1/admin/flags'),
  setAdminFlag: (key: string, enabled: boolean) => req<FeatureFlag>(`/v1/admin/flags/${encodeURIComponent(key)}`, { method: 'PUT', body: { enabled } }),
  // v7 person insights (admin)
  insightsCoverage: () => req<InsightsCoverage>('/v1/admin/insights/coverage'),

  // v8 coins & gifts (SPEC_V8, ARCHITECTURE §27). 404 feature_disabled while `gifts` is off.
  wallet: () => req<Wallet>('/v1/me/wallet'),
  walletHistory: (cursor?: string) => req<{ entries: LedgerEntry[]; next_cursor: string | null }>(`/v1/me/wallet/history${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`),
  claimDailyCoins: () => req<{ coins: number; wallet: Wallet }>('/v1/wallet/daily', { method: 'POST' }),
  giftCatalog: () => req<GiftCatalog>('/v1/gifts/catalog'),
  sendGift: (b: SendGiftInput) => req<Gift>('/v1/gifts', { method: 'POST', body: b }),
  removeGift: (id: string) => req<Gift>(`/v1/gifts/${id}/remove`, { method: 'POST' }),
  thankGift: (id: string) => req<Gift>(`/v1/gifts/${id}/thanks`, { method: 'POST' }),
  acceptGift: (id: string) => req<Gift>(`/v1/gifts/${id}/accept`, { method: 'POST' }),
  declineGift: (id: string) => req<Gift>(`/v1/gifts/${id}/decline`, { method: 'POST' }),
  voidGift: (id: string, reason: VoidReason) => req<Gift>(`/v1/gifts/${id}/void`, { method: 'POST', body: { reason } }),
  /** Counts a finished photo strip as booth_completed (the images never leave the device). */
  giftCaptured: (id: string, done: { layout: string; theme: string; shots: number }) => req<void>(`/v1/gifts/${id}/captured`, { method: 'POST', body: done }),
  giftShelf: (dir: 'received' | 'sent') => req<Shelf>(`/v1/me/gifts?dir=${dir}`),
  adminGrantCoins: (userId: string, delta: number, reason: string) =>
    req<{ grant: { id: string; delta: number; reason: string }; wallet: Wallet }>(`/v1/admin/users/${userId}/coins`, { method: 'POST', body: { delta, reason } }),
};

export { ApiError };
